import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { ApiError, api, fetchHealth } from '../lib/api';
import type { AdminStats, HealthStatus } from '../types';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FullPageLoader } from '../components/ui/spinner';
import { ActivityIcon, DatabaseIcon, ShieldIcon, UsersIcon } from '../components/icons';
import { displayNameOf, formatDateTime } from '../lib/utils';

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const { t, locale } = useTranslation();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [healthResult, statsResult] = await Promise.all([
        fetchHealth(),
        api.get<AdminStats>('/admin/stats'),
      ]);
      setHealth(healthResult);
      setStats(statsResult);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('admin.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !stats) {
    return <FullPageLoader label={t('admin.loading')} />;
  }

  const databaseOk = health?.checks.database === 'ok';

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldIcon className="h-5 w-5 text-brand" />
            <h1 className="text-2xl font-semibold tracking-tight text-fg">{t('admin.title')}</h1>
          </div>
          <p className="mt-1 text-sm text-muted">{t('admin.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={() => void load()} loading={loading}>
          {t('admin.refresh')}
        </Button>
      </div>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              {t('admin.signedInAs')}
            </p>
            <p className="mt-1 truncate text-sm font-semibold text-fg">
              {user ? displayNameOf(user) : '\u2014'}
            </p>
            <div className="mt-2">
              <Badge tone="brand">
                {user ? t(user.role === 'admin' ? 'role.admin' : 'role.user') : '\u2014'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted">
              <UsersIcon className="h-4 w-4" />
              <p className="text-xs font-medium uppercase tracking-wide">{t('admin.users')}</p>
            </div>
            <p className="mt-2 text-2xl font-semibold text-fg">{stats?.users.total ?? '\u2014'}</p>
            <p className="mt-1 text-xs text-muted">
              {stats
                ? t('admin.usersSummary', {
                    admins: stats.users.admins,
                    active: stats.users.active,
                  })
                : '\u2014'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted">
              <ActivityIcon className="h-4 w-4" />
              <p className="text-xs font-medium uppercase tracking-wide">{t('admin.apiLabel')}</p>
            </div>
            <div className="mt-2">
              <Badge tone={health?.ok ? 'success' : 'danger'}>
                {t(health?.ok ? 'admin.apiHealthy' : 'admin.apiUnavailable')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted">
              <DatabaseIcon className="h-4 w-4" />
              <p className="text-xs font-medium uppercase tracking-wide">{t('admin.database')}</p>
            </div>
            <div className="mt-2">
              <Badge tone={!health ? 'neutral' : databaseOk ? 'success' : 'danger'}>
                {health
                  ? t(databaseOk ? 'admin.databaseOk' : 'admin.databaseUnavailable')
                  : t('admin.databaseUnknown')}
              </Badge>
            </div>
            <p className="mt-2 text-xs text-muted">
              {stats?.database.serverTime
                ? t('admin.serverTime', { date: formatDateTime(stats.database.serverTime, locale) })
                : '\u2014'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.session.title')}</CardTitle>
            <CardDescription>{t('admin.session.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted">{t('admin.field.email')}</span>
              <span className="break-all font-medium text-fg">{user?.email ?? '\u2014'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted">{t('admin.field.role')}</span>
              <Badge tone="brand">
                {user ? t(user.role === 'admin' ? 'role.admin' : 'role.user') : '\u2014'}
              </Badge>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted">{t('admin.field.accountId')}</span>
              <span className="break-all font-mono text-xs text-fg">{user?.id ?? '\u2014'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted">{t('admin.field.statusChecked')}</span>
              <span className="text-xs text-muted">
                {formatDateTime(stats?.generatedAt, locale)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('admin.roadmap.title')}</CardTitle>
            <CardDescription>{t('admin.roadmap.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted">
              {[
                'admin.roadmap.contests',
                'admin.roadmap.questions',
                'admin.roadmap.payments',
                'admin.roadmap.results',
                'admin.roadmap.users',
              ].map((key) => (
                <li key={key} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                  <span>{t(key as 'admin.roadmap.contests')}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
